import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, subMonths } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { PACKAGING_USAGE, SHIPHERO_LABEL_COST } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';

const PAGE_SIZE = 100;

const REPORTS = {
  [SHIPHERO_LABEL_COST]: {
    title: 'ShipHero Label Cost (Monthly)',
    description: 'One priced label per order shipped in the month (Pacific time). $0 extra-box labels count as packages, not cost.',
    statuses: ['priced', 'missing_cost', 'zero_cost'],
    cards: [['label_cost', 'Label cost', 'currency'], ['orders', 'Orders', 'number'], ['packages', 'Packages', 'number'], ['exceptions', 'Exceptions', 'number']],
    empty: 'No ShipHero labels in the selected month.',
  },
  [PACKAGING_USAGE]: {
    title: 'Packaging Usage (Monthly)',
    description: 'Every box shipped in the month (Pacific time), including extra boxes on multi-box orders, mapped to packaging SKU and weighted cost.',
    statuses: null,
    cards: [['total_cost', 'Packaging cost', 'currency'], ['boxes', 'Boxes', 'number'], ['no_box_labels', 'Packages with no box recorded', 'number'], ['exceptions', 'Exceptions', 'number']],
    empty: 'No boxes shipped in the selected month.',
  },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const formatValue = (value, type) => {
  if (value === null || value === undefined || value === '') return '—';
  if (type === 'currency') return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  if (type === 'number') return Number(value).toLocaleString('en-US');
  if (type === 'date') {
    const parsed = parseISO(String(value).slice(0, 10));
    return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'EEEE, MMMM do, yyyy');
  }
  return value;
};

const ShipheroMonthlyCostReport = ({ reportId, marginFromSidebar }) => {
  const config = REPORTS[reportId];
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const [month, setMonth] = useState(format(subMonths(new Date(), 1), 'yyyy-MM'));
  const [status, setStatus] = useState('all');
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totals, setTotals] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useCallback((includePage = true) => {
    const query = new URLSearchParams({ month });
    if (includePage) {
      query.set('page', page);
      query.set('pageSize', PAGE_SIZE);
    }
    if (status !== 'all') query.set('status', status);
    return query;
  }, [month, page, status]);

  const load = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/reports/${reportId}?${params()}`, { headers: authHeaders(), signal });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setColumns(json.columns || []);
      setTotals(json.totals || {});
      setTotalRecords(json.totalRecords || 0);
      setTotalPages(json.totalPages || 1);
    } catch (requestError) {
      if (requestError?.name === 'AbortError' || !isLatest(requestId)) return;
      setError(requestError.message);
      setData([]);
    } finally {
      if (isLatest(requestId)) setLoading(false);
    }
  }, [beginRequest, isLatest, params, reportId]);

  useEffect(() => {
    if (initialReady) load();
  }, [initialReady, load]);

  useEffect(() => setPage(1), [month, status, reportId]);

  const exportData = async () => {
    const response = await fetch(`/api/v1/reports/${reportId}/export?${params(false)}`, { headers: authHeaders() });
    if (!response.ok) throw new Error('Failed to export report');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportId}-${month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return { handled: true };
  };

  const filters = (
    <div className="contents">
      <label className="flex items-center gap-2 text-sm">
        <span>Month</span>
        <input type="month" className="rounded border px-3 py-2 text-sm" value={month} onChange={(event) => event.target.value && setMonth(event.target.value)} />
      </label>
      {config.statuses && (
        <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">All statuses</option>
          {config.statuses.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
      )}
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between text-sm">
      <span>{totalRecords.toLocaleString()} rows</span>
      <div className="flex gap-2">
        <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
        <span className="py-1">Page {page} of {totalPages}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
      </div>
    </div>
  );

  return (
    <ReportTemplate
      reportId={reportId}
      title={config.title}
      description={config.description}
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={<CSVExportButton data={data} columns={columns} filename={`${reportId}-${month}`} onExport={exportData} />}
      pagination={pagination}
      renderChildrenOnly
    >
      <div className="space-y-3">
        <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
          {config.cards.map(([key, label, type]) => (
            <div key={key} className="rounded bg-gray-50 p-3">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="font-semibold">{formatValue(totals[key], type)}</div>
            </div>
          ))}
        </div>
        {Array.isArray(totals.by_carrier) && totals.by_carrier.length > 0 && (
          <div className="grid gap-3 rounded border bg-white p-4 md:grid-cols-4">
            {totals.by_carrier.map((row) => (
              <div key={row.carrier} className="rounded bg-gray-50 p-3">
                <div className="text-xs text-gray-500">{row.carrier} ({formatValue(row.orders, 'number')} orders)</div>
                <div className="font-semibold">{formatValue(row.label_cost, 'currency')}</div>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-auto rounded border bg-white">
          {data.length === 0 ? <div className="px-6 py-12 text-center text-sm text-gray-500">{config.empty}</div> : (
            <table className="min-w-max text-left text-xs">
              <thead className="sticky top-0 bg-gray-100"><tr>{columns.map((column) => <th key={column.key} className="whitespace-nowrap px-3 py-2">{column.label}</th>)}</tr></thead>
              <tbody>{data.map((row, index) => <tr key={`${row.order_number || row.box_name}-${index}`} className={index % 2 ? 'bg-gray-50' : ''}>
                {columns.map((column) => <td key={column.key} className="whitespace-nowrap px-3 py-2">{formatValue(row[column.key], column.type)}</td>)}
              </tr>)}</tbody>
            </table>
          )}
        </div>
      </div>
    </ReportTemplate>
  );
};

export const ShipheroLabelCostReport = (props) => <ShipheroMonthlyCostReport {...props} reportId={SHIPHERO_LABEL_COST} />;
export const PackagingUsageReport = (props) => <ShipheroMonthlyCostReport {...props} reportId={PACKAGING_USAGE} />;

export default ShipheroMonthlyCostReport;
